'use client';

import { useEffect, useRef } from 'react';

import { useReducedMotion } from 'framer-motion';

import { NaumuFace, type FaceState } from './naumu-face';

import { cn } from '../utils/cn';

export type { FaceState };

interface NaumuFaceViewProps {
	/**
	 * Desired state. Semi-controlled - the engine may move on by itself
	 * (`surprised` lands in `attentive` after ~1.4s, `wink` lands in `idle`
	 * after ~0.9s) and reports it via
	 * `onStateChange`, so a parent holding this in state can follow along.
	 */
	state?: FaceState;
	/** Global tempo multiplier for hold durations and spring stiffness. */
	speed?: number;
	onStateChange?: (state: FaceState) => void;
	/** Sizes the face - the svg fills this box. Color inherits via `currentColor`. */
	className?: string;
}

/**
 * Animated Naumu logo face. Two parametric shapes (arc brow + rounded-rect eye)
 * chase per-state keyframes through underdamped springs, so any state change
 * interpolates from wherever the face currently is. Use `LogoFace` instead on
 * static branding surfaces and in lists - each instance here owns a rAF loop.
 *
 * The engine draws with `overflow: visible` because the round caps intentionally
 * poke past the 12x12 grid, so don't wrap this in a tight `overflow-hidden` box.
 */
export const NaumuFaceView = ({
	state = 'idle',
	speed = 1,
	onStateChange,
	className,
}: NaumuFaceViewProps) => {
	const mountRef = useRef<HTMLDivElement>(null);
	const faceRef = useRef<NaumuFace | null>(null);
	const onStateChangeRef = useRef(onStateChange);
	onStateChangeRef.current = onStateChange;

	const prefersReducedMotion = useReducedMotion();
	const effectiveState: FaceState = prefersReducedMotion ? 'static' : state;

	// Seeds the engine's starting pose so it doesn't spring in from `idle` on mount.
	const initialStateRef = useRef(effectiveState);

	// One engine instance per component lifetime; prop changes go through setters.
	useEffect(() => {
		const mount = mountRef.current;
		if (!mount) return;

		const face = new NaumuFace(mount, {
			size: '100%',
			color: 'currentColor',
			state: initialStateRef.current,
			onChange: (next) => onStateChangeRef.current?.(next),
		});
		faceRef.current = face;

		return () => {
			face.destroy();
			faceRef.current = null;
		};
	}, []);

	useEffect(() => {
		faceRef.current?.setState(effectiveState);
	}, [effectiveState]);

	useEffect(() => {
		faceRef.current?.setSpeed(speed);
	}, [speed]);

	// A long message list can mount dozens of faces; only the visible ones run,
	// and none while this window is not the focused one (the loop is a rAF the
	// compositor cannot take over, unlike the CSS breathing).
	useEffect(() => {
		const mount = mountRef.current;
		if (!mount) return;
		let visible = true;
		let focused = document.hasFocus();
		const sync = () => {
			if (visible && focused) faceRef.current?.resume();
			else faceRef.current?.pause();
		};
		const observer = new IntersectionObserver(([entry]) => {
			visible = entry.isIntersecting;
			sync();
		});
		observer.observe(mount);
		const onFocus = () => {
			focused = true;
			sync();
		};
		const onBlur = () => {
			focused = false;
			sync();
		};
		window.addEventListener('focus', onFocus);
		window.addEventListener('blur', onBlur);
		sync();
		return () => {
			observer.disconnect();
			window.removeEventListener('focus', onFocus);
			window.removeEventListener('blur', onBlur);
		};
	}, []);

	return <div ref={mountRef} aria-hidden="true" className={cn('shrink-0', className)} />;
};
